import {
  Flex,
  IconArrowOpenDownLine,
  IconArrowOpenUpLine,
  IconButton,
  IconEditLine,
  IconTrashLine,
  Pill,
  Text,
  ToggleDetails,
  Tooltip,
} from "@instructure/ui";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApDataContext } from "../../../../views/Plan";
import { Card } from "../../../Card";
import { DialogYesNo } from "../../../DialogYesNo/DialogYesNo";
import { rteHasContent } from "../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../RichTextArea/RichTextViewer";
import { MeasureForm } from "../ComponentForms/MeasureForm";

const QualitativeMeasureCard = ({ measure, lastMeasure, inTemplate }) => {
  const {
    measureIndex,
    sectionIndex,
    handleDeleteMeasure,
    setSections,
    extraData,
  } = useContext(ApDataContext);
  const { t } = useTranslation();
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const moveMeasureUp = () => {
    setSections((prev) =>
      prev.map((section, i) =>
        i === sectionIndex
          ? {
              ...section,
              measures: section.measures.map((m, j) =>
                j === measureIndex
                  ? section.measures[measureIndex - 1]
                  : j === measureIndex - 1
                    ? section.measures[measureIndex]
                    : m
              ),
            }
          : section
      )
    );
  };

  const moveMeasureDown = () => {
    setSections((prev) =>
      prev.map((section, i) =>
        i === sectionIndex
          ? {
              ...section,
              measures: section.measures.map((m, j) =>
                j === measureIndex
                  ? section.measures[measureIndex + 1]
                  : j === measureIndex + 1
                    ? section.measures[measureIndex]
                    : m
              ),
            }
          : section
      )
    );
  };

  return (
    <>
      <Card style={{ backgroundColor: "#FFFFFF" }}>
        <Flex wrap="wrap" alignItems="start">
          <Flex.Item>
            <Pill color="info">{t("assessment.qualitativeMeasure")}</Pill>
          </Flex.Item>

          {!inTemplate && (
            <Flex.Item shouldGrow textAlign="end">
              <Tooltip
                renderTip={t("measures.moveMeasureUp")}
                on={["hover", "focus"]}
              >
                <IconButton
                  renderIcon={IconArrowOpenUpLine}
                  screenReaderLabel={t("measures.moveMeasureUp")}
                  size="small"
                  onClick={moveMeasureUp}
                  disabled={measureIndex === 0}
                />
              </Tooltip>
            </Flex.Item>
          )}

          {!inTemplate && (
            <Flex.Item margin="0 small">
              <Tooltip
                renderTip={t("measures.moveMeasureDown")}
                on={["hover", "focus"]}
              >
                <IconButton
                  renderIcon={IconArrowOpenDownLine}
                  screenReaderLabel={t("measures.moveMeasureDown")}
                  size="small"
                  onClick={moveMeasureDown}
                  disabled={!!lastMeasure}
                />
              </Tooltip>
            </Flex.Item>
          )}
          <Flex.Item shouldGrow={inTemplate} textAlign={inTemplate && "end"}>
            <Tooltip renderTip={t("measures.edit")} on={["hover", "focus"]}>
              <IconButton
                size="small"
                renderIcon={IconEditLine}
                screenReaderLabel={t("measures.edit")}
                onClick={() => setEditModal(true)}
              />
            </Tooltip>
          </Flex.Item>
          {!inTemplate && (
            <Flex.Item margin="0 0 0 small">
              <Tooltip renderTip={t("measures.delete")} on={["hover", "focus"]}>
                <IconButton
                  color="danger"
                  size="small"
                  screenReaderLabel={t("measures.delete")}
                  renderIcon={IconTrashLine}
                  onClick={() => setDeleteModal(true)}
                />
              </Tooltip>
            </Flex.Item>
          )}

          <Flex.Item size="100%" padding="x-small 0 0 0">
            <Text as="div" size="small" lineHeight="double">
              <Text weight="bold" size="small">
                {t("assessment.name")}:{" "}
              </Text>
              {measure.name}
            </Text>
          </Flex.Item>
          {rteHasContent(measure.instructions) && (
            <Flex.Item size="100%" padding="xxx-small 0 xxx-small 0">
              <ToggleDetails
                summary={
                  <Text weight="bold" size="small">
                    {t("assessment.instructions")}
                  </Text>
                }
              >
                <RichTextViewer
                  value={measure.instructions}
                  ariaLabel={t("assessment.instructions")}
                />
              </ToggleDetails>
            </Flex.Item>
          )}
          <Flex.Item size="100%" padding="0 0 0 0">
            <Text as="div" size="small" lineHeight="double">
              <Text weight="bold" size="small">
                {t("assessment.goal")}:{" "}
              </Text>
              {measure.goal.description}
            </Text>
            <Text as="div" size="small" lineHeight="double">
              <Text weight="bold" size="small">
                {t("assessment.objective")}:{" "}
              </Text>
              {measure.objective?.description
                ? measure.objective.description
                : "None"}
            </Text>
            <Text as="div" size="small" lineHeight="double">
              <Text weight="bold" size="small">
                {t("measures.uploadEvidence")}?:{" "}
              </Text>
              {measure.upload_evidence
                ? t("dialogYesNo.yes")
                : t("dialogYesNo.no")}
            </Text>
            {measure.assignees && (
              <Text as="div" size="small" lineHeight="double">
                <Text weight="bold" size="small">
                  {t("assessment.reviewers")}:{" "}
                </Text>
                {measure.assignees.split(",").join(", ")}
              </Text>
            )}
          </Flex.Item>
        </Flex>
      </Card>
      <DialogYesNo
        message={t("measures.deleteConfirm")}
        openStatus={deleteModal}
        onClickNo={() => setDeleteModal(false)}
        onClickYes={() => handleDeleteMeasure(measure, sectionIndex)}
      />
      <MeasureForm
        openModal={editModal}
        setOpenModal={setEditModal}
        courseLevels={extraData.courseLevels}
        assignmentTypes={extraData.assignmentTypes}
        acSubs={extraData.acSubs}
        planId={extraData.plan?.id}
        allCycles={extraData.cycles}
        programs={extraData.programs}
        measure={measure}
        inTemplate={inTemplate}
      />
    </>
  );
};

export default QualitativeMeasureCard;
